import { Guid, Nillable } from '@web/utils';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { AdminMultimediaImageUpload } from '@web/shared/module/multimedia';
import { AdminBoxStatus } from '../enums/admin-box-status';
import { AdminBoxCosmetic } from './admin-box-cosmetic';

export class AdminBoxAttributes {
  public constructor(
    private readonly data: {
      items: Array<AdminBoxAttributeItem>;
    },
  ) {}

  public get items(): Array<AdminBoxAttributeItem> {
    return this.data.items;
  }
}

export type AdminBoxAttributeItem = {
  content: string;
  id?: Nillable<Guid>;
  productAttributeId: Nillable<Guid>;
};
export type AdminBoxAttributeFormGroup = FormGroup<{
  content: FormControl<string>;
  id: FormControl<Nillable<Guid>>;
  productAttributeId: FormControl<Nillable<Guid>>;
}>;
export type AdminBoxAttributesFormGroup = FormGroup<{
  attributes: FormArray<AdminBoxAttributeFormGroup>;
}>;

export class AdminBoxCharacteristics {
  public constructor(
    private readonly data: {
      items: Array<AdminBoxCharacteristicItem>;
    },
  ) {}

  public get items(): Array<AdminBoxCharacteristicItem> {
    return this.data.items;
  }
}

export type AdminBoxCharacteristicItem = {
  content: string;
  id?: Nillable<Guid>;
  label: string;
};

export type AdminBoxCharacteristicFormGroup = FormGroup<{
  content: FormControl<string>;
  id: FormControl<Nillable<Guid>>;
  label: FormControl<string>;
}>;

export type AdminBoxCharacteristicsFormGroup = FormGroup<{
  characteristics: FormArray<AdminBoxCharacteristicFormGroup>;
}>;

export class AdminBoxDetailsForm {
  public static NameMaxLength = 255;
  public static PasswordMaxLength = 255;

  public constructor(
    private readonly data: {
      attributes?: Nillable<AdminBoxAttributes>;
      characteristics?: Nillable<AdminBoxCharacteristics>;
      boxCategoryId?: Nillable<Guid>;
      canBeWarehouseProductIdEdited?: Nillable<boolean>;
      cosmeticBrandId?: Nillable<Guid>;
      cosmetics?: Nillable<AdminBoxCosmetic[]>;
      featuredFile?: Nillable<AdminMultimediaImageUpload>;
      files?: Nillable<AdminMultimediaImageUpload[]>;
      illustrationFile?: Nillable<AdminMultimediaImageUpload>;
      name: string;
      password?: Nillable<string>;
      price?: Nillable<number>;
      availableQuantity?: Nillable<number>;
      slug?: Nillable<string>;
      status: AdminBoxStatus;
      symbolFile?: Nillable<AdminMultimediaImageUpload>;
      maxQuantityPerOrder?: Nillable<number>;
      contentPreviewUrl?: Nillable<string>;
      currentEdition?: Nillable<boolean>;
      publishedAt?: Nillable<Date>;
      id?: Nillable<Guid>;
      warehouseProductId?: Nillable<Guid>;
    },
  ) {}

  public get name(): string {
    return this.data.name;
  }

  public get password(): Nillable<string> {
    return this.data.password;
  }

  public get slug(): Nillable<string> {
    return this.data.slug;
  }

  public get featuredFile(): Nillable<AdminMultimediaImageUpload> {
    return this.data.featuredFile;
  }

  public get symbolFile(): Nillable<AdminMultimediaImageUpload> {
    return this.data.symbolFile;
  }

  public get illustrationFile(): Nillable<AdminMultimediaImageUpload> {
    return this.data.illustrationFile;
  }

  public get files(): Nillable<AdminMultimediaImageUpload[]> {
    return this.data.files;
  }

  public get status(): AdminBoxStatus {
    return this.data.status;
  }

  public get cosmeticBrandId(): Nillable<Guid> {
    return this.data.cosmeticBrandId;
  }

  public get cosmetics(): Nillable<AdminBoxCosmetic[]> {
    return this.data.cosmetics;
  }

  public get boxCategoryId(): Nillable<Guid> {
    return this.data.boxCategoryId;
  }

  public get warehouseProductId(): Nillable<Guid> {
    return this.data.warehouseProductId;
  }

  public get price(): Nillable<number> {
    return this.data.price;
  }

  public get availableQuantity(): Nillable<number> {
    return this.data.availableQuantity;
  }

  public get maxQuantityPerOrder(): Nillable<number> {
    return this.data.maxQuantityPerOrder;
  }

  public get contentPreviewUrl(): Nillable<string> {
    return this.data.contentPreviewUrl;
  }

  public get currentEdition(): Nillable<boolean> {
    return this.data.currentEdition;
  }

  public get publishedAt(): Nillable<Date> {
    return this.data.publishedAt;
  }

  public get characteristics(): Nillable<AdminBoxCharacteristics> {
    return this.data.characteristics;
  }

  public get attributes(): Nillable<AdminBoxAttributes> {
    return this.data.attributes;
  }

  public get id(): Nillable<Guid> {
    return this.data.id;
  }

  public get canBeWarehouseProductIdEdited(): Nillable<boolean> {
    return this.data.canBeWarehouseProductIdEdited;
  }
}

export type AdminProductDetailsFormGroup = FormGroup<{
  attributes: FormControl<Nillable<AdminBoxAttributes>>;
  characteristics: FormControl<Nillable<AdminBoxCharacteristics>>;
  cosmetics: FormControl<Nillable<AdminBoxCosmetic[]>>;
  featuredFile: FormControl<Nillable<AdminMultimediaImageUpload>>;
  symbolFile: FormControl<Nillable<AdminMultimediaImageUpload>>;
  illustrationFile: FormControl<Nillable<AdminMultimediaImageUpload>>;
  files: FormControl<Nillable<AdminMultimediaImageUpload[]>>;
  name: FormControl<string>;
  slug: FormControl<Nillable<string>>;
  password: FormControl<Nillable<string>>;
  price: FormControl<Nillable<number>>;
  availableQuantity: FormControl<Nillable<number>>;
  status: FormControl<Nillable<AdminBoxStatus>>;
  cosmeticBrandId: FormControl<Nillable<Guid>>;
  boxCategoryId: FormControl<Nillable<Guid>>;
  warehouseProductId: FormControl<Nillable<Guid>>;
  maxQuantityPerOrder: FormControl<Nillable<number>>;
  contentPreviewUrl: FormControl<Nillable<string>>;
  currentEdition: FormControl<Nillable<boolean>>;
  publishedAt: FormControl<Nillable<Date>>;
}>;
